<template>
  <div class="immo-container">
    <div class="page-head">
      <div>
        <div class="page-kicker">数据 · 防盗快捷版</div>
        <h1 class="page-title">VIN 模式查询</h1>
        <p class="page-desc">按 VIN 前 11 位检索。每条记录独立保存 8 个字段，车辆参数以该行为准。</p>
      </div>
      <div class="head-actions">
        <el-tooltip content="上传格式正确的 .txt 文件。每行 8 个字段，以制表符分隔" placement="bottom">
          <el-button
            :disabled="!isDeveloper || btLoading2"
            :loading="btLoading2"
            :icon="Upload"
            @click="openFileInput"
          >
            {{ btLoading2 ? '插入中...' : '批量导入' }}
          </el-button>
        </el-tooltip>
        <el-button type="primary" :disabled="!isDeveloper" :icon="Plus" @click="openForm">
          添加模式
        </el-button>
        <input ref="fileInput" type="file" class="file-input" accept=".txt" @change="handleFileChange" />
      </div>
    </div>

    <section class="query-card">
      <div class="search-label">
        VIN 前 11 位
        <el-tag size="small" effect="plain" class="info-tag">必填 11 位</el-tag>
      </div>

      <div class="search-row">
        <el-input
          class="vin-input"
          size="large"
          :model-value="searchVIN"
          maxlength="11"
          clearable
          placeholder="输入 VIN 前 11 位"
          @update:model-value="onVINInput"
          @keyup.enter="fetchData"
        >
          <template #suffix>
            <span class="vin-count">{{ searchVIN.length }} / 11</span>
          </template>
        </el-input>

        <el-button type="primary" class="query-btn" :loading="loading" @click="fetchData">
          查询
        </el-button>

        <div class="history-wrap">
          <el-popover
            v-if="vinHistory.length > 0"
            v-model:visible="historyVisible"
            placement="bottom-end"
            :width="332"
            trigger="click"
            popper-class="immo-history-popper"
          >
            <template #reference>
              <button type="button" class="history-btn history-trigger">
                <el-icon><Timer /></el-icon>
                历史
                <span class="history-count">{{ vinHistory.length }}</span>
              </button>
            </template>
            <div class="history-panel">
              <div class="history-panel-head">
                <h3>VIN 历史记录</h3>
                <span class="history-cap">{{ vinHistory.length }} / {{ historyLimit }}</span>
              </div>
              <p class="history-panel-hint">查询成功后写入本机，点击即可再次查询</p>
              <button
                v-for="(vin, index) in vinHistory"
                :key="vin"
                type="button"
                class="history-item"
                :class="{ current: vin === searchVIN }"
                @click="selectHistoryVIN(vin)"
              >
                <span class="history-vin">{{ vin }}</span>
                <span v-if="vin === searchVIN" class="history-now">当前</span>
                <el-icon class="delete-history" title="删除" @click.stop="removeHistoryItem(index)">
                  <Close />
                </el-icon>
              </button>
              <div class="history-panel-foot">
                <span>最多保存 {{ historyLimit }} 条</span>
                <button type="button" class="clear-link" @click="clearAllHistory">清空全部</button>
              </div>
            </div>
          </el-popover>
          <button
            v-else
            type="button"
            class="history-btn history-trigger"
            disabled
            title="暂无历史记录"
          >
            <el-icon><Timer /></el-icon>
            历史
          </button>
        </div>
      </div>

      <div v-if="vinHistory.length" class="history-strip">
        最近查询
        <button
          v-for="(vin, index) in vinHistory"
          :key="'chip-' + vin"
          type="button"
          class="history-chip"
          :class="{ active: vin === searchVIN }"
          @click="selectHistoryVIN(vin)"
        >
          {{ vin }}
          <el-icon class="chip-x" @click.stop="removeHistoryItem(index)">
            <Close />
          </el-icon>
        </button>
        <span class="hist-meta">本机 {{ vinHistory.length }} / {{ historyLimit }}</span>
        <button type="button" class="clear-link" @click="clearAllHistory">清空</button>
      </div>
    </section>

    <div v-if="!loading && !userRequestParameters.length" class="status-tip">
      <el-empty :description="searchVIN ? '未找到匹配的VIN数据' : '请输入VIN后查询数据'" :image-size="80">
        <template #image>
          <el-icon class="empty-icon" :size="40">
            <Search />
          </el-icon>
        </template>
      </el-empty>
    </div>

    <section v-if="userRequestParameters.length" class="dossier" v-loading="loading">
      <div class="dossier-top">
        <div class="vin-hero">
          <div class="vin-groups">
            <div class="vin-group">
              <span>前 3 位</span>
              <b class="vin-cell">{{ queriedVinParts.prefix3 }}</b>
            </div>
            <div v-if="queriedVinParts.isComplete" class="vin-group">
              <span>中 5 位</span>
              <b class="vin-cell">{{ queriedVinParts.mid5 }}</b>
            </div>
            <div v-if="queriedVinParts.isComplete" class="vin-group">
              <span>后 3 位</span>
              <b class="vin-cell">{{ queriedVinParts.suffix3 }}</b>
            </div>
          </div>
          <div class="vin-meta">VIN 前 11 位匹配</div>
        </div>
        <div class="stat">
          <strong>{{ userRequestParameters.length }}</strong>
          <span>条独立记录</span>
        </div>
      </div>

      <div class="mode-title">记录列表</div>
      <div class="record-list">
        <article
          v-for="(row, index) in userRequestParameters"
          :key="row.ID || index"
          class="record"
        >
          <div class="record-head">
            <div class="mode-code" :class="{ primary: index === 0 }">{{ row.Mode }}</div>
            <div class="record-title">
              <strong>ModeText <b>{{ formatImmoFieldValue(row.ModeText) }}</b></strong>
              <span>创建时间 {{ formatImmoFieldValue(row.CreatedAt) }}</span>
            </div>
            <div class="record-actions">
              <el-button size="small" class="action-icon-btn btn-edit" :disabled="!isDeveloper" @click="editItem(row)">
                编辑
              </el-button>
              <el-popconfirm title="确定删除此条记录吗？" @confirm="deleteItem(row.ID)">
                <template #reference>
                  <el-button size="small" class="action-icon-btn btn-del" :disabled="!isDeveloper">
                    删除
                  </el-button>
                </template>
              </el-popconfirm>
            </div>
          </div>
          <div class="spec-grid">
            <div
              v-for="field in buildImmoRecordFields(row)"
              :key="field.key"
              class="spec"
              :class="{ empty: field.empty }"
            >
              <label>{{ field.label }}</label>
              <code>{{ field.value }}</code>
            </div>
          </div>
        </article>
      </div>
    </section>

    <el-dialog
      v-model="showForm"
      :title="formMode === '添加' ? '添加VIN数据' : '编辑VIN数据'"
      width="600px"
      class="custom-dialog"
      destroy-on-close
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="100px" class="dialog-form" @submit.prevent="submitForm">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="VIN" required :error="errors.VIN">
              <el-input v-model="form.VIN" placeholder="11位VIN" maxlength="11" show-word-limit
                :class="{ 'is-error': errors.VIN }" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Region" required :error="errors.REGION">
              <el-input v-model="form.REGION" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Vehicle" required :error="errors.Vehicle">
              <el-input v-model="form.Vehicle" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Year" :error="errors.Year">
              <el-input v-model="form.Year" placeholder="年份或范围" maxlength="9" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="YearRange" :error="errors.YearRange">
              <el-input v-model="form.YearRange" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Feature" :error="errors.Feature">
              <el-input v-model="form.Feature" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="ModeText" required :error="errors.ModeText">
              <el-input v-model="form.ModeText" placeholder="8位文本ID" maxlength="8" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Mode" required :error="errors.Mode">
              <el-input v-model="form.Mode" placeholder="4位模式号" maxlength="4" show-word-limit />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeForm">取消</el-button>
          <el-button type="primary" @click="submitForm" :loading="btLoading2">确 定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { addImmoMode, updateImmoMode, getImmoMode, deleteImmoMode } from '../api/users';
import { useStore } from 'vuex';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Upload, Timer, Close } from '@element-plus/icons-vue';
import {
  VIN_IMMO_HISTORY_LIMIT,
  splitVinPrefix11,
  formatImmoFieldValue,
  buildImmoRecordFields,
} from '../utils/vinImmoPresentation.mjs';

export default {
  data() {
    return {
      userRequestParameters: [],
      loading: false,
      searchVIN: '',
      vinHistory: [],
      historyVisible: false,
      form: {
        VIN: '',
        REGION: '',
        Vehicle: '',
        Year: '',
        YearRange: '',
        Feature: '',
        ModeText: '',
        Mode: ''
      },
      formMode: '添加',
      currentID: null,
      showForm: false,
      currentUserRole: '',
      btLoading2: false,
      errors: {}
    };
  },
  computed: {
    Search: () => Search,
    Plus: () => Plus,
    Upload: () => Upload,
    Timer: () => Timer,
    Close: () => Close,
    isDeveloper() {
      return this.currentUserRole === '开发';
    },
    historyLimit: () => VIN_IMMO_HISTORY_LIMIT,
    queriedVin() {
      if (this.searchVIN && this.searchVIN.length === 11) return this.searchVIN;
      return this.userRequestParameters[0]?.VIN || '';
    },
    queriedVinParts() {
      return splitVinPrefix11(this.queriedVin);
    },
  },
  methods: {
    formatImmoFieldValue,
    buildImmoRecordFields,
    rememberVin(vin) {
      if (!vin || vin.length !== 11 || this.vinHistory.includes(vin)) return;
      this.vinHistory.unshift(vin);
      if (this.vinHistory.length > VIN_IMMO_HISTORY_LIMIT) {
        this.vinHistory.pop();
      }
      localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
    },

    async fetchData() {
      if (this.searchVIN && this.searchVIN.length !== 11) {
        ElMessage.warning('VIN必须为11位');
        return;
      }

      this.loading = true;
      try {
        if (!this.searchVIN) {
          this.userRequestParameters = [];
          return;
        }

        const response = await getImmoMode({ vin: this.searchVIN.slice(0, 11) });
        this.userRequestParameters = response.data.tableData || [];
        this.rememberVin(this.searchVIN);

        if (this.userRequestParameters.length === 0) {
          ElMessage.info('未找到匹配的VIN数据');
        } else {
          ElMessage.success('查询成功');
        }
      } catch (error) {
        console.error(error);
        ElMessage.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },

    async deleteItem(id) {
      try {
        await deleteImmoMode(id);
        ElMessage.success('删除成功');
        this.fetchData();
      } catch (error) {
        ElMessage.error('删除失败');
        console.error(error);
      }
    },

    editItem(item) {
      this.formMode = '修改';
      this.form = { ...item };
      this.currentID = item.ID;
      this.showForm = true;
    },

    onVINInput(value) {
      this.searchVIN = String(value ?? '').toUpperCase();
    },

    selectHistoryVIN(vin) {
      this.searchVIN = vin;
      this.historyVisible = false;
      this.fetchData();
    },

    removeHistoryItem(index) {
      this.vinHistory.splice(index, 1);
      localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
      if (this.vinHistory.length === 0) {
        this.historyVisible = false;
      }
      ElMessage.success('已从历史记录中删除');
    },

    clearAllHistory() {
      ElMessageBox.confirm('确定清空所有历史记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.vinHistory = [];
        this.historyVisible = false;
        localStorage.removeItem('vinHistory');
        ElMessage.success('历史记录已清空');
      }).catch(() => { });
    },

    loadVINHistory() {
      const savedHistory = localStorage.getItem('vinHistory');
      if (savedHistory) {
        try {
          const parsed = JSON.parse(savedHistory);
          this.vinHistory = parsed.filter(vin =>
            vin && typeof vin === 'string' && vin.trim() !== '' && vin.length === 11
          ).slice(0, VIN_IMMO_HISTORY_LIMIT);

          if (this.vinHistory.length !== parsed.length) {
            localStorage.setItem('vinHistory', JSON.stringify(this.vinHistory));
          }
        } catch (e) {
          console.error('解析历史记录失败', e);
          this.vinHistory = [];
          localStorage.removeItem('vinHistory');
        }
      }
    },
    validateForm() {
      this.form.VIN = this.form.VIN?.toUpperCase() || '';
      this.form.REGION = this.form.REGION?.toUpperCase() || '';
      this.form.Vehicle = this.form.Vehicle?.toUpperCase() || '';
      this.form.Year = this.form.Year?.toUpperCase() || '';
      this.form.YearRange = this.form.YearRange?.toUpperCase() || '';
      this.form.Feature = this.form.Feature?.toUpperCase() || '';
      this.form.ModeText = this.form.ModeText?.toUpperCase() || '';
      this.form.Mode = this.form.Mode?.toUpperCase() || '';

      this.errors = {};
      const regex = {
        VIN: /^[A-HJ-NPR-Z0-9?]{11}$/,
        REGION: /^[0-9A-F]{8}$/,
        Vehicle: /^[0-9A-F]{8}$/,
        Year: /^(\d{4}|-\d{4}|\d{4}-\d{4}|\d{4}-$|^)$/,
        YearRange: /^[0-9A-F]{8}$/,
        Feature: /^[0-9A-F]{8}$/,
        ModeText: /^[0-9A-F]{8}$/,
        Mode: /^[0-9A-F]{4}$/
      };

      if (!this.form.VIN || !regex.VIN.test(this.form.VIN)) {
        this.errors.VIN = 'VIN必须是11位的合法字符串';
      }
      if (!this.form.REGION || !regex.REGION.test(this.form.REGION)) {
        this.errors.REGION = 'Region必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.Vehicle || !regex.Vehicle.test(this.form.Vehicle)) {
        this.errors.Vehicle = 'Vehicle必须是8位的数字或字母（0-9, A-F）';
      }
      if (this.form.Year !== "NULL" && this.form.Year !== "" && !regex.Year.test(this.form.Year)) {
        this.errors.Year = 'Year格式不正确';
      }
      if (this.form.YearRange && !regex.YearRange.test(this.form.YearRange)) {
        this.errors.YearRange = 'YearRange必须是8位的数字或字母（0-9, A-F）';
      }
      if (this.form.Feature !== "NULL" && this.form.Feature !== "" && !regex.Feature.test(this.form.Feature)) {
        this.errors.Feature = 'Feature必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.ModeText || !regex.ModeText.test(this.form.ModeText)) {
        this.errors.ModeText = 'ModeText必须是8位的数字或字母（0-9, A-F）';
      }
      if (!this.form.Mode || !regex.Mode.test(this.form.Mode)) {
        this.errors.Mode = 'Mode必须是4位的数字或字母（0-9, A-F）';
      }

      return Object.keys(this.errors).length === 0;
    },

    async submitForm() {
      if (!this.validateForm()) {
        ElMessage.error('表单验证失败，请检查输入');
        return;
      }

      this.btLoading2 = true;
      try {
        if (!this.form.Year || this.form.Year === "NULL") {
          this.form.Year = "NULL";
          this.form.YearRange = "AAAA00F6";
        }
        if (!this.form.Feature) {
          this.form.Feature = "NULL";
        }

        if (this.formMode === '添加') {
          await addImmoMode(this.form);
          ElMessage.success('添加成功');
        } else {
          await updateImmoMode(this.form);
          ElMessage.success('修改成功');
        }

        this.fetchData();
        this.closeForm();
      } catch (error) {
        ElMessage.error(`${this.formMode}失败`);
        console.error('Error during submission:', error);
      } finally {
        this.btLoading2 = false;
      }
    },

    openForm() {
      this.formMode = '添加';
      this.form = {
        VIN: '',
        REGION: '',
        Vehicle: '',
        Year: '',
        YearRange: '',
        Feature: '',
        ModeText: '',
        Mode: ''
      };
      this.errors = {};
      this.showForm = true;
    },

    closeForm() {
      this.showForm = false;
      this.errors = {};
    },

    openFileInput() {
      if (this.btLoading2) return;
      this.$refs.fileInput.value = null;
      this.$nextTick(() => {
        this.$refs.fileInput.click();
      });
    },

    async handleFileChange(event) {
      this.btLoading2 = true;
      const file = event.target.files[0];
      if (!file) {
        this.btLoading2 = false;
        ElMessage.warning('请选择一个文件');
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const fileContent = e.target.result;
        const records = this.parseFileContent(fileContent);

        if (records.length === 0) {
          this.btLoading2 = false;
          ElMessage.warning('文件内容无效，请检查文件格式');
          return;
        }

        try {
          await this.batchInsertData(records);
        } catch (error) {
          console.error('批量插入失败:', error);
        } finally {
          this.btLoading2 = false;
        }
      };
      reader.onerror = () => {
        this.btLoading2 = false;
        ElMessage.error('文件读取失败');
      };
      reader.readAsText(file);
    },

    parseFileContent(content) {
      const lines = content.trim().split('\n');
      const records = [];
      const regex = {
        VIN: /^[A-HJ-NPR-Z0-9?]{11}$/,
        REGION: /^[0-9A-F]{8}$/,
        Vehicle: /^[0-9A-F]{8}$/,
        Year: /^(\d{4}|-\d{4}|\d{4}-\d{4}|\d{4}-$|^)$/,
        YearRange: /^[0-9A-F]{8}$/,
        Feature: /^[0-9A-F]{8}$/,
        ModeText: /^[0-9A-F]{8}$/,
        Mode: /^[0-9A-F]{4}$/
      };

      let errorCount = 0;

      for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const line = lines[lineIndex];
        const fields = line.split('\t').map(field => field.trim());

        if (fields.length === 8) {
          const [VIN, REGION, Vehicle, Year, YearRange, Feature, ModeText, Mode] = fields;

          const record = {
            VIN,
            REGION,
            Vehicle,
            YearRange,
            Year,
            Feature,
            ModeText,
            Mode
          };

          if (!record.Year || record.Year === "NULL") {
            record.Year = "NULL";
            record.YearRange = "AAAA00F6";
          }
          if (!record.Feature) {
            record.Feature = "NULL";
          }

          Object.keys(record).forEach(key => {
            if (record[key]) record[key] = record[key].toUpperCase();
          });

          const lineErrors = [];

          if (!record.VIN || !regex.VIN.test(record.VIN)) {
            lineErrors.push(`VIN格式错误`);
          }
          if (!record.REGION || !regex.REGION.test(record.REGION)) {
            lineErrors.push(`Region格式错误`);
          }
          if (!record.Vehicle || !regex.Vehicle.test(record.Vehicle)) {
            lineErrors.push(`Vehicle格式错误`);
          }
          if (record.Year !== "NULL" && record.Year !== "" && !regex.Year.test(record.Year)) {
            lineErrors.push(`Year格式错误`);
          }
          if (record.YearRange && !regex.YearRange.test(record.YearRange)) {
            lineErrors.push(`YearRange格式错误`);
          }
          if (record.Feature !== "NULL" && record.Feature !== "" && !regex.Feature.test(record.Feature)) {
            lineErrors.push(`Feature格式错误`);
          }
          if (!record.ModeText || !regex.ModeText.test(record.ModeText)) {
            lineErrors.push(`ModeText格式错误`);
          }
          if (!record.Mode || !regex.Mode.test(record.Mode)) {
            lineErrors.push(`Mode格式错误`);
          }

          if (lineErrors.length > 0) {
            errorCount++;
            ElMessage.error(`第 ${lineIndex + 1} 行数据格式错误：${lineErrors.join('、')}`);
          } else {
            records.push(record);
            errorCount = 0;
          }

          if (errorCount >= 10) {
            ElMessage.error('连续10条数据出错，操作已终止');
            return [];
          }
        } else {
          ElMessage.error(`第 ${lineIndex + 1} 行字段数不正确`);
          return [];
        }
      }

      return records;
    },

    async batchInsertData(records) {
      this.btLoading2 = true;
      try {
        await addImmoMode(records);
        ElMessage.success(`${records.length}条记录插入成功`);
        this.fetchData();
      } catch (error) {
        ElMessage.error(`插入失败：${error.message || '未知错误'}`);
        console.error('Failed to insert records:', error);
      } finally {
        this.btLoading2 = false;
      }
    }
  },

  mounted() {
    this.loadVINHistory();
    this.fetchData();
    const store = useStore();
    this.currentUserRole = store.state.userData?.job;
  },
};
</script>

<style scoped>
.immo-container {
  padding: 16px 22px 28px;
  background: var(--wf-page-bg, #f5f7fb);
  color: var(--wf-text-primary, #0f172a);
  min-height: 100%;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.page-head > :first-child {
  min-width: 0;
  flex: 1 1 240px;
}

.page-kicker {
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 4px;
}

.page-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: var(--wf-text-primary, #0f172a);
}

.page-desc {
  margin: 6px 0 0;
  color: var(--wf-text-muted, #64748b);
  font-size: 13px;
  line-height: 1.5;
  max-width: 560px;
}

.head-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  flex-wrap: wrap;
}

.file-input {
  display: none;
}

.query-card {
  background: var(--wf-surface, #fff);
  border: 1px solid var(--wf-border, #e6eef7);
  border-radius: 16px;
  padding: 16px 18px 14px;
  margin-bottom: 14px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.search-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: var(--wf-text-primary, #1e293b);
  font-size: 13px;
  font-weight: 800;
}

.info-tag {
  background: var(--wf-status-info-bg, #eff6ff);
  color: var(--wf-status-info-text, #1d4ed8);
  border: none;
  border-radius: 30px;
}

.search-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vin-input {
  width: 280px;
  flex: 0 0 auto;
}

.vin-input :deep(.el-input__wrapper) {
  border-radius: 12px;
  background: var(--wf-surface-soft, #f8fafc);
  border: 1px solid var(--wf-control-border, #dbe4f0);
  box-shadow: none;
  padding-left: 12px;
}

.vin-input :deep(.el-input__inner) {
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.vin-count {
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.query-btn {
  height: 40px;
  padding: 0 22px;
  border-radius: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.history-wrap {
  margin-left: auto;
  flex-shrink: 0;
}

.history-trigger {
  height: 32px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--wf-control-border, #d7e0ea);
  background: var(--wf-surface, #fff);
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.history-trigger:disabled {
  opacity: 0.55;
  cursor: default;
}

.history-trigger:not(:disabled):hover {
  border-color: #93c5fd;
  background: var(--wf-status-info-bg, #eff6ff);
  color: var(--wf-status-info-text, #1d4ed8);
}

.history-count {
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.history-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  flex-wrap: wrap;
}

.history-chip {
  height: 28px;
  padding: 0 8px 0 10px;
  border-radius: 999px;
  border: 1px solid var(--wf-border, #e2e8f0);
  background: var(--wf-surface, #fff);
  color: var(--wf-text-regular, #334155);
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.history-chip.active {
  border-color: #93c5fd;
  background: var(--wf-status-info-bg, #eff6ff);
  color: var(--wf-status-info-text, #1d4ed8);
}

.chip-x {
  color: #94a3b8;
  font-size: 12px;
}

.hist-meta {
  margin-left: auto;
  color: var(--wf-text-muted, #94a3b8);
  font-size: 12px;
  font-weight: 700;
}

.clear-link {
  border: 0;
  background: transparent;
  color: var(--wf-status-danger-text, #b91c1c);
  font-size: 12px;
  font-weight: 800;
  padding: 0;
  cursor: pointer;
}

.status-tip {
  margin-top: 8px;
  padding: 32px 0;
  background: var(--wf-surface-soft, #f8fafc);
  border-radius: 16px;
}

.empty-icon {
  color: var(--wf-text-muted, #94a3b8);
}

.dossier {
  background: var(--wf-surface, #fff);
  border: 1px solid var(--wf-border, #e6eef7);
  border-radius: 16px;
  padding: 18px 20px 16px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.dossier-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--wf-border, #eef2f7);
}

.vin-groups {
  display: flex;
  align-items: flex-end;
  gap: 14px;
}

.vin-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vin-group span {
  color: var(--wf-text-muted, #94a3b8);
  font-size: 11px;
  font-weight: 700;
}

.vin-cell {
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-size: 28px;
  letter-spacing: 1.5px;
  line-height: 1;
  color: var(--wf-text-primary, #0f172a);
}

.vin-meta {
  margin-top: 8px;
  color: var(--wf-text-muted, #64748b);
  font-size: 13px;
}

.stat {
  text-align: right;
}

.stat strong {
  display: block;
  font-size: 22px;
  font-weight: 800;
  color: var(--wf-status-info-text, #1d4ed8);
  line-height: 1;
}

.stat span {
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 700;
}

.mode-title {
  margin: 16px 0 10px;
  color: var(--wf-text-regular, #334155);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record {
  padding: 14px 16px 12px;
  border: 1px solid var(--wf-border, #e8eef6);
  border-radius: 14px;
  background: var(--wf-surface, #fff);
}

.record-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.mode-code {
  width: 72px;
  height: 36px;
  border-radius: 10px;
  background: #eef2ff;
  color: #3730a3;
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-size: 16px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}

.mode-code.primary {
  background: #2563eb;
  color: #fff;
}

.record-title {
  flex: 1;
  min-width: 0;
}

.record-title strong {
  display: block;
  font-size: 13px;
  color: var(--wf-text-primary, #0f172a);
}

.record-title b {
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-weight: 800;
  margin-left: 6px;
}

.record-title span {
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 600;
}

.record-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.btn-edit {
  color: #1d4ed8;
  border-color: #bfdbfe;
  background: #f8fbff;
}

.btn-del {
  color: #b91c1c;
  border-color: #fecaca;
  background: #fff;
}

.spec-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.spec {
  padding: 8px 10px;
  border-radius: 12px;
  background: var(--wf-surface-soft, #f8fafc);
  border: 1px solid var(--wf-border, #eef2f7);
}

.spec label {
  display: block;
  color: var(--wf-text-muted, #94a3b8);
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
}

.spec code {
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--wf-text-primary, #0f172a);
}

.spec.empty code {
  color: var(--wf-text-muted, #94a3b8);
  font-weight: 600;
}

.custom-dialog :deep(.el-dialog) {
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
}

.custom-dialog :deep(.el-dialog__header) {
  background: var(--wf-surface-soft, #f8fafc);
  padding: 20px 24px;
  margin: 0;
  border-bottom: 1px solid var(--wf-border, #e9eef4);
}

.custom-dialog :deep(.el-dialog__title) {
  color: var(--wf-text-primary, #0f172a);
  font-weight: 600;
  font-size: 18px;
}

.custom-dialog :deep(.el-dialog__body) {
  padding: 28px 24px;
}

.custom-dialog :deep(.el-dialog__footer) {
  padding: 16px 24px 24px;
  border-top: 1px solid var(--wf-border, #e9eef4);
  background: var(--wf-surface-soft, #f8fafc);
}

.dialog-form :deep(.el-form-item__label) {
  color: var(--wf-text-regular, #475569);
  font-weight: 500;
  font-size: 13px;
}

.dialog-form :deep(.el-input__wrapper) {
  border-radius: 12px;
  border: 1px solid var(--wf-control-border, #e2e8f0);
  box-shadow: none;
  padding: 4px 12px;
}

.dialog-form :deep(.is-error .el-input__wrapper) {
  border-color: #ef4444;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

@media screen and (max-width: 768px) {
  .immo-container {
    padding: 16px;
  }

  .page-head {
    flex-direction: column;
    align-items: stretch;
  }

  .head-actions {
    justify-content: flex-end;
  }

  .search-row {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
  }

  .vin-input {
    width: 100%;
    grid-column: 1 / -1;
  }

  .history-wrap {
    justify-self: end;
  }

  .hist-meta {
    margin-left: 0;
  }

  .spec-grid {
    grid-template-columns: 1fr 1fr;
  }

  .vin-cell {
    font-size: 22px;
  }

  .record-head {
    flex-wrap: wrap;
  }
}
</style>

<style>
.immo-history-popper {
  padding: 12px !important;
  border-radius: 16px !important;
  border: 1px solid var(--wf-border, #e6eef7) !important;
  box-shadow: 0 22px 48px rgba(15, 23, 42, 0.16) !important;
}

.immo-history-popper .history-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 10px;
}

.immo-history-popper .history-panel-head h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 800;
  color: var(--wf-text-primary, #0f172a);
}

.immo-history-popper .history-cap {
  color: var(--wf-text-muted, #64748b);
  font-size: 12px;
  font-weight: 700;
}

.immo-history-popper .history-panel-hint {
  margin: 0 6px 10px;
  color: var(--wf-text-muted, #94a3b8);
  font-size: 12px;
}

.immo-history-popper .history-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--wf-text-regular, #334155);
  cursor: pointer;
  text-align: left;
}

.immo-history-popper .history-item.current {
  background: var(--wf-status-info-bg, #eff6ff);
  color: var(--wf-status-info-text, #1d4ed8);
}

.immo-history-popper .history-vin {
  flex: 1;
  font-family: 'Cascadia Code', 'SF Mono', Consolas, monospace;
  font-size: 13px;
  font-weight: 800;
}

.immo-history-popper .history-now {
  height: 20px;
  padding: 0 7px;
  border-radius: 999px;
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
}

.immo-history-popper .delete-history {
  color: #94a3b8;
}

.immo-history-popper .history-panel-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding: 8px 6px 2px;
  border-top: 1px solid var(--wf-border, #eef2f7);
  color: var(--wf-text-muted, #94a3b8);
  font-size: 12px;
}

.immo-history-popper .clear-link {
  border: 0;
  background: transparent;
  color: var(--wf-status-danger-text, #b91c1c);
  font-size: 12px;
  font-weight: 800;
  padding: 0;
  cursor: pointer;
}
</style>
